import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type Live,
  type Today,
  fetchLive,
  fetchToday,
  getSession,
} from "../api";
import { DeviceStatusCard } from "../components/DeviceStatusCard";
import { EnergyFlow } from "../components/EnergyFlow";
import { HomeEarnings } from "../components/HomeEarnings";
import { MetricSeriesCard } from "../components/MetricSeriesCard";
import { Modal } from "../components/Modal";
import { TodayTiles } from "../components/TodayTiles";
import { IconButton, Skeleton } from "../components/ui";
import { greeting } from "../format";
import { usePoll } from "../usePoll";

const LIVE_POLL_MS = 5_000;
const TODAY_POLL_MS = 60_000;

/**
 * Overview tab, v2 composition: greeting header, HERO energy flow
 * (embeds SOC + battery state), EUR earnings row (money first), today
 * tiles, device status. Polling happens here so the children stay
 * pure presenters; monotonic sequence counters keep a slow /api/live
 * response from overwriting a newer one.
 * docs/customer-app.md > Front-end > Home tab
 */
export function Home() {
  const navigate = useNavigate();
  const session = getSession();
  // usePoll: wist de fout zodra iets lukt, tolereert een enkele blip
  // (hervatten uit de achtergrond!) en pauzeert terwijl het scherm weg
  // is - zie src/usePoll.ts.
  const { data: live, error: liveErr } = usePoll<Live>(fetchLive, LIVE_POLL_MS);
  const { data: today, error: todayErr } = usePoll<Today>(fetchToday, TODAY_POLL_MS);
  const error = liveErr ?? todayErr;
  const [detailNode, setDetailNode] = useState<
    "pv" | "load" | "battery" | "grid" | "ev" | null
  >(null);

  useEffect(() => {
    if (!session) navigate("/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="tab-enter stagger px-4 pt-6 pb-28 max-w-3xl mx-auto flex flex-col gap-5 lg:max-w-6xl lg:px-8 lg:pt-10 lg:pb-12 lg:grid lg:grid-cols-2 lg:items-start">
      <header className="flex items-center justify-between lg:col-span-2">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src="/favicon.png"
            alt=""
            width={40}
            height={40}
            className="shrink-0 select-none lg:hidden"
            draggable={false}
          />
          <div className="min-w-0">
            <div className="text-sm text-ink-mute leading-tight">
              {greeting()}
              {session?.name ? `, ${session.name}` : ""}
            </div>
            <div className="text-[22px] font-bold tracking-tight leading-tight">
              Mijn SlimStroom
            </div>
          </div>
        </div>
        <IconButton ariaLabel="Instellingen" onClick={() => navigate("/account")}>
          <GearIcon />
        </IconButton>
      </header>

      {error && (
        <div className="text-sm text-danger bg-danger-soft rounded-2xl p-3 border border-card-border lg:col-span-2">
          {error}
        </div>
      )}

      {/* HERO - live flow with SOC + battery-state built in. Op lg
          staat de hero links en vult de rechterkolom zich met de
          geld/tegel/status-kaarten (row-span over die drie rijen). */}
      <div className="lg:row-span-3">
      {live ? (
        <EnergyFlow live={live} today={today} onNodeClick={setDetailNode} />
      ) : (
        <Skeleton className="h-[420px] rounded-3xl" />
      )}
      </div>

      {/* Money-first row - kWh detail follows underneath. */}
      <HomeEarnings />

      {today ? (
        <TodayTiles today={today} onMetricTap={setDetailNode} />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-28 col-span-2" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      )}

      {/* Edge-device liveness - self-fetches + renders its own state. */}
      <DeviceStatusCard />

      {/* Node drill-in: any flow disc (and the Today tiles) opens a
          modal with that node's power graph. Batterij and Net split
          into their two directions; the Auto graph is the wallbox
          draw as recorded by de EMS (per ~15 min). */}
      <Modal
        open={detailNode !== null}
        onClose={() => setDetailNode(null)}
        title={
          detailNode === "pv" ? "Opgewekt"
          : detailNode === "load" ? "Verbruikt"
          : detailNode === "battery" ? "Batterij"
          : detailNode === "grid" ? "Net"
          : detailNode === "ev" ? "Auto laden"
          : ""
        }
      >
        {detailNode === "pv" && (
          <MetricSeriesCard metric="pv" initialRange="6h" chartMode="bar" />
        )}
        {detailNode === "load" && (
          <MetricSeriesCard metric="load" initialRange="6h" chartMode="bar" />
        )}
        {detailNode === "battery" && (
          <div className="flex flex-col gap-3">
            <MetricSeriesCard metric="battery_in" initialRange="6h" chartMode="bar" chartHeight={120} />
            <MetricSeriesCard metric="battery_out" initialRange="6h" chartMode="bar" chartHeight={120} />
          </div>
        )}
        {detailNode === "grid" && (
          <div className="flex flex-col gap-3">
            <MetricSeriesCard metric="grid_in" initialRange="6h" chartMode="bar" chartHeight={120} />
            <MetricSeriesCard metric="grid_out" initialRange="6h" chartMode="bar" chartHeight={120} />
          </div>
        )}
        {detailNode === "ev" && (
          <div className="flex flex-col gap-3">
            {live?.ev && (
              <div className="card p-3 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="font-semibold truncate">
                    {live.ev.vehicle || "Auto"}
                  </div>
                  <div className="text-xs text-ink-mute">
                    {!live.ev.connected
                      ? "niet aangesloten"
                      : live.ev.charging
                        ? `laadt met ${(live.ev.charge_power_w / 1000).toFixed(1)} kW`
                        : "aangesloten, laadt niet"}
                  </div>
                </div>
                {live.ev.connected && live.ev.vehicle_soc_pct != null && (
                  <div className="text-right shrink-0">
                    <div className="display-num text-2xl" style={{ color: "var(--color-ev)" }}>
                      {Math.round(live.ev.vehicle_soc_pct)}%
                    </div>
                    <div className="text-xs text-ink-mute">accu auto</div>
                  </div>
                )}
              </div>
            )}
            <MetricSeriesCard metric="ev" initialRange="6h" chartMode="bar" />
          </div>
        )}
      </Modal>
    </div>
  );
}

function GearIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}
